import { Component, EventEmitter, Input, OnInit, Output, AfterViewInit, OnDestroy, ElementRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { PreventableEvent } from '@progress/kendo-angular-dropdowns';
import { BaseFilterCellComponent, FilterService } from '@progress/kendo-angular-grid';
import { Subject } from 'rxjs';

@Component({
  selector: 'kendo-drop-down-filter',
  templateUrl: './kendo-drop-down-filter.component.html',
  styleUrls: ['./kendo-drop-down-filter.component.scss'],
  host: {
    'role': 'combobox',
    '[attr.aria-label]': 'ariaLabel',
    '[attr.aria-expanded]': 'isOpen',
    '[attr.aria-controls]': 'id'
  },
  providers: [
    {provide: NG_VALUE_ACCESSOR, useExisting: KendoDropDownFilterComponent, multi: true}
  ]
})
export class KendoDropDownFilterComponent extends BaseFilterCellComponent implements OnInit, OnDestroy, ControlValueAccessor {


  @Input('id') public id: string;
  @Input('name') public name: string;
  @Input('placeholder') public placeholder: string;
  @Input('textField') public textField: string;
  @Input('valueField') public valueField: string;
  @Input() public field: string;
   @Input('aria-label') ariaLabel: string;
  @Input() title: string
  @Output('open') open: EventEmitter<PreventableEvent> = new EventEmitter<PreventableEvent>();
  @Output('close') close: EventEmitter<PreventableEvent> = new EventEmitter<PreventableEvent>();
  @Output('focus') focus: EventEmitter<any> = new EventEmitter<any>();
  @Output('blur') blur: EventEmitter<any> = new EventEmitter<any>();
  @Output('valueChanged') valueChanged: EventEmitter<any> = new EventEmitter();
  public sourceList: any[] = [];
  innerValue: string;
  public isOpen: boolean = false;
  @Input() clearInputValue: Subject<any>;
  private observer: MutationObserver;

  constructor(filterService: FilterService, private elementRef: ElementRef) {
    super(filterService);
  }

  public _multiple: boolean = false;

  @Input('multiple')
  set multiple(value: boolean) {
    this._multiple = true;
  };

  public _optionList: any[] = [];

  get optionList(): any[] {
    return this._optionList;
  }

  @Input('optionList')
  set optionList(list: any[]) {
    if (list) {
      this._optionList = list;
      this.sourceList = JSON.parse(JSON.stringify(list));
    }
  }

  ngOnInit(): void {
    if (this.clearInputValue) {
      this.clearInputValue.asObservable().subscribe(() => {
        this.innerValue = undefined;
      });
    }
    // Set up MutationObserver to fix invalid aria-activedescendant values for Axe issue
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        
        if (mutation.type === 'attributes' && mutation.attributeName === 'aria-activedescendant') {
          const target = mutation.target as HTMLElement;
          const value = target.getAttribute('aria-activedescendant');
          if (value && value.endsWith('-undefined')) {
            // Remove the invalid aria-activedescendant attribute
            target.removeAttribute('aria-activedescendant');
          }
        }

        // Fix for Axe issue: kendo-taglist role="listbox" should not contain ul, but options
        if (mutation.type === 'childList' || mutation.type === 'attributes') {
          const taglists = this.elementRef.nativeElement.querySelectorAll('kendo-taglist[role="listbox"]');
          taglists.forEach((taglist: HTMLElement) => {
            const ul = taglist.querySelector('ul');
            if (ul && !ul.hasAttribute('role')) {
              ul.setAttribute('role', 'presentation');
            }
            const lis = taglist.querySelectorAll('li');
            lis.forEach((li: HTMLElement) => {
              if (!li.hasAttribute('role')) {
                li.setAttribute('role', 'option');
              }
            });
          });

          // Fix for Wave issue: kendo-searchbar input should not have role="listbox" and needs aria-label
          const searchbars = this.elementRef.nativeElement.querySelectorAll('kendo-searchbar');
          searchbars.forEach((searchbar: HTMLElement) => {
            const input = searchbar.querySelector('input');
            if (input) {
              if (input.getAttribute('role') === 'listbox') {
                input.removeAttribute('role');
              }
              if (!input.hasAttribute('aria-label')) {
                input.setAttribute('aria-label', this.ariaLabel || 'Search options');
              }
              // Remove invalid ARIA attributes for input
              if (input.hasAttribute('aria-expanded')) {
                input.removeAttribute('aria-expanded');
              }
              if (input.hasAttribute('aria-haspopup')) {
                input.removeAttribute('aria-haspopup');
              }
            }
          });
        }
      });
    });

    // Start observing the component's native element and its subtree
    const config = { attributes: true, attributeFilter: ['aria-activedescendant'], subtree: true, childList: true };
    this.observer.observe(this.elementRef.nativeElement, config);
  }

  ngOnDestroy(): void {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  propagateChange = (_: any) => {
  };

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(): void {
  }

  writeValue(value: any): void {
    if (value && this.innerValue !== value) {
      this.innerValue = value;
      this.propagateChange(value);
    } else {
      this.innerValue = null;
    }
  }

  valueChange = (event: any[]) => {
    this.propagateChange(event);
    this.valueChanged.emit(event);
    if (this.field) {
      this.applyFilter(
        event === null ?
          this.removeFilter(this.field) :
          this.updateFilter({
            field: this.field,
            operator: 'eq',
            value: event
          })
      );
    }
  };

  public searchChange(filter: string): void {
    if (this._multiple && !this.valueField) {
      this.filterList(filter);
    } else if (this._multiple && this.valueField) {
      this.filterList(filter, this.textField);
    } else if (!this._multiple && !this.valueField) {
      this.filterList(filter);
    } else if (!this._multiple && this.valueField) {
      this.filterList(filter, this.textField);
    }
  }

  filterList(filter: string, key?: string): void {
    if (key) {
      this._optionList = this.sourceList.filter((s) => {
        return s[key].toLowerCase().indexOf(filter.toLowerCase()) !== -1;
      });
    } else {
      this._optionList = this.sourceList.filter((s) => {
        return s.toLowerCase().indexOf(filter.toLowerCase()) !== -1;
      });
    }
  }

  onOpen = (event: PreventableEvent): void => {
    this.isOpen = true;
    this.open.emit(event);
  };
  onClose = (event: PreventableEvent): void => {
    this.isOpen = false;
    this.close.emit(event);
  };
  onFocus = (event: any): void => {
    this.focus.emit(event);
  };
  onBlur = (event: any): void => {
    this.blur.emit(event);
  };
}
